import React, { useState } from 'react';
import './Donation.css';
import { useLanguage } from '../context/LanguageContext';
import Toast from '../components/Toast';

const Donation = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    amount: '',
    purpose: 'general',
    customPurpose: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, text: null });
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
  };

  const formatIndianCurrency = (num) => {
    if (!num) return '';
    const n = num.toString().replace(/,/g, '');
    if (isNaN(n)) return num;
    const parts = n.split('.');
    let lastThree = parts[0].substring(parts[0].length - 3);
    const otherBlocks = parts[0].substring(0, parts[0].length - 3);
    if (otherBlocks !== '') {
      lastThree = ',' + lastThree;
    }
    const formatted = otherBlocks.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
    return parts.length > 1 ? formatted + '.' + parts[1] : formatted;
  };

  const validateForm = () => {
    const { name, email, phone, amount } = formData;

    // Name validation
    if (name.trim().length < 3) {
      return t('validation.nameError');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.emailError');
    }

    // Phone validation (exactly 10 digits and starts with 6-9)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      return t('validation.phoneError');
    }
    if (!/^[6-9]/.test(phoneDigits)) {
      return t('validation.phoneStartError');
    }

    // Amount validation
    const cleanAmount = amount.toString().replace(/,/g, '');
    if (!cleanAmount || parseFloat(cleanAmount) <= 0) {
      return t('validation.amountError');
    }

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Strict numeric only for phone and amount
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').substring(0, 10);
      setFormData({ ...formData, [name]: numericValue });
      return;
    }

    if (name === 'amount') {
      const numericValue = value.replace(/\D/g, '');
      setFormData({ ...formData, [name]: formatIndianCurrency(numericValue) });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      amount: '',
      purpose: 'general',
      customPurpose: '',
      message: ''
    });
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }

    setLoading(true);
    setStatus({ type: null, text: null });

    try {
      const res = await fetch('https://mhaskobaadmin.tipic.in/api/donate/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          amount: formData.amount.toString().replace(/,/g, ''),
          purpose: formData.purpose === 'other' ? formData.customPurpose : formData.purpose
        }),
      });

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || 'ऑर्डर तयार करताना त्रुटी');
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Razorpay लोड झाला नाही');
      }

      const options = {
        key: data.key,
        amount: data.amount,
        currency: 'INR',
        name: 'श्री श्रीनाथ म्हस्कोबा महाराज मंदिर',
        description: `दान - ${data.purpose}`,
        order_id: data.order_id,
        prefill: {
          name: data.name,
          email: data.email,
          contact: data.phone,
        },
        notes: {
          purpose: data.purpose,
        },
        theme: { color: '#F37254' },
        handler: async function (response) {

          resetForm();
          // Payment successful → verify
          const verifyRes = await fetch('https://mhaskobaadmin.tipic.in/api/donate/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              amount: data.amount,
              name: data.name,
              email: data.email,
              phone: data.phone,
              purpose: data.purpose,
              customPurpose: formData.customPurpose,
              message: formData.message,

            }),
          });

          const verifyData = await verifyRes.json();

          if (verifyData.success) {
            showToast(verifyData.message, 'success');
            setFormData({ name: '', email: '', phone: '', amount: '', purpose: 'general', customPurpose: '', message: '' });
          } else {
            showToast('पेमेंट यशस्वी झाले पण सत्यापनात त्रुटी आली.', 'error');
          }
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err) {
      showToast(err.message || 'काहीतरी चूक झाली. पुन्हा प्रयत्न करा.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="donation-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <section className="donation-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <span className="hero-pill">{t('pageContent.donateNow')}</span>
          <h1>{t('donation.pageTitle')}</h1>
          <div className="header-divider">
            <span className="dot"></span>
            <span className="line"></span>
            <span className="dot"></span>
          </div>
        </div>
      </section>

      <div className="container">
        <section className="donation-intro">
          <p>
            {t('donation.intro')}
          </p>
        </section>

        <div className="donation-content">
          <div className="donation-form-section">
            <h2>{t('donation.formTitle')}</h2>
            {/* Status alerts removed in favor of Toasts */}
            <form className="donation-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('donation.fullName')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.fullNamePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('donation.emailAddress')} *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('donation.phoneNumber')} *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.phonePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('donation.donationAmount')} *</label>
                <div className="amount-input-wrapper">
                  <span className="currency-symbol">₹</span>
                  <input
                    type="text"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('donation.donationPurpose')} *</label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  required
                >
                  <option value="general">{t('donation.donationPurposes.general')}</option>
                  <option value="annadan">{t('donation.donationPurposes.annadan')}</option>
                  <option value="development">{t('donation.donationPurposes.development')}</option>
                  <option value="gaushala">{t('donation.donationPurposes.gaushala')}</option>
                  <option value="festival">{t('donation.donationPurposes.festival')}</option>
                  <option value="maintenance">{t('donation.donationPurposes.maintenance')}</option>
                  <option value="other">{t('donation.donationPurposes.other')}</option>
                </select>
              </div>

              {formData.purpose === 'other' && (
                <div className="form-group animate-in">
                  <label>{t('donation.donationPurpose')} (Custom) *</label>
                  <input
                    type="text"
                    name="customPurpose"
                    value={formData.customPurpose}
                    onChange={handleChange}
                    required
                    placeholder={t('donation.otherPurposePlaceholder')}
                  />
                </div>
              )}

              <div className="form-group">
                <label>{t('donation.message')}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  placeholder={t('contact.messagePlaceholder')}
                ></textarea>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Processing...' : t('donation.proceedToPayment')}
              </button>
            </form>
          </div>

          <div className="donation-info-section">
            <div className="info-card">
              <h3>{t('donation.quickDonationOptions')}</h3>
              <div className="quick-amounts">
                <button className="amount-btn" onClick={() => setFormData({ ...formData, amount: '500' })}>₹500</button>
                <button className="amount-btn" onClick={() => setFormData({ ...formData, amount: '1,000' })}>₹1,000</button>
                <button className="amount-btn" onClick={() => setFormData({ ...formData, amount: '2,500' })}>₹2,500</button>
                <button className="amount-btn" onClick={() => setFormData({ ...formData, amount: '5,000' })}>₹5,000</button>
                <button className="amount-btn" onClick={() => setFormData({ ...formData, amount: '10,000' })}>₹10,000</button>
                <button className="amount-btn custom-btn" onClick={() => {
                  setFormData({ ...formData, amount: '' });
                  document.getElementsByName('amount')[0]?.focus();
                }}>{t('donation.customAmount')}</button>
              </div>
            </div>

            <div className="info-card">
              <h3>{t('donation.upiPayment')}</h3>
              <div className="qr-placeholder">
                <span className="qr-icon">📲</span>
                <p>{t('donation.scanQRCode')}</p>
              </div>
              <p className="upi-id">{t('donation.upiId')}</p>
            </div>

            <div className="info-card">
              <h3>{t('donation.bankDetails')}</h3>
              <div className="bank-details">
                <p><strong>{t('donation.accountName')}</strong></p>
                <p><strong>{t('donation.accountNumber')}</strong></p>
                <p><strong>{t('donation.ifscCode')}</strong></p>
                <p><strong>{t('donation.bank')}</strong></p>
                <p><strong>{t('donation.branch')}</strong></p>
              </div>
            </div>

            <div className="info-card tax-benefit">
              <h3>{t('donation.taxBenefits')}</h3>
              <p>
                {t('donation.taxText')}
              </p>
            </div>
          </div>
        </div>

        <section className="donation-impact">
          <h2 className="section-title">{t('donation.howYourDonationHelps')}</h2>
          <div className="header-divider section-divider">
            <span className="dot"></span>
            <span className="line"></span>
            <span className="dot"></span>
          </div>

          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-card-header">
                <div className="impact-icon">🍽️</div>
                <h3>{t('donation.annadan.title')}</h3>
              </div>
              <p>{t('donation.annadan.description')}</p>
            </div>
            <div className="impact-card">
              <div className="impact-card-header">
                <div className="impact-icon">🏛️</div>
                <h3>{t('donation.templeDevelopment.title')}</h3>
              </div>
              <p>{t('donation.templeDevelopment.description')}</p>
            </div>
            <div className="impact-card">
              <div className="impact-card-header">
                <div className="impact-icon">🐄</div>
                <h3>{t('donation.gauSeva.title')}</h3>
              </div>
              <p>{t('donation.gauSeva.description')}</p>
            </div>
            <div className="impact-card">
              <div className="impact-card-header">
                <div className="impact-icon">🎉</div>
                <h3>{t('donation.festivals.title')}</h3>
              </div>
              <p>{t('donation.festivals.description')}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Donation;
